import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const formatUSD = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

export function GerenciarCadastroExteriorModal({ onClose, onSuccess }) {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados do formulário
  const [empresa, setEmpresa] = useState('');
  const [codigo, setCodigo] = useState('');
  const [setor, setSetor] = useState('');
  const [categoria, setCategoria] = useState('');
  
  // Estados de edição
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});

  useEffect(() => {
    carregarEmpresas();
  }, []);

  // Preencher automaticamente "Imobiliário" quando categoria for "REIT's"
  useEffect(() => {
    if (categoria === "REIT's") {
      setSetor('Imobiliário');
    }
  }, [categoria]);

  const carregarEmpresas = async () => {
    try {
      const response = await axios.get(`${API}/empresas/Wall Street`, getAuthHeaders());
      setEmpresas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!empresa || !codigo || !setor || !categoria) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      if (editingId) {
        // Atualizar empresa existente
        await axios.put(`${API}/empresas/${editingId}`, {
          empresa,
          codigo,
          setor,
          categoria
        }, getAuthHeaders());
        toast.success('Ativo atualizado com sucesso');
        setEditingId(null);
      } else {
        // Criar nova empresa
        await axios.post(`${API}/empresas`, {
          template: 'Wall Street',
          empresa,
          codigo,
          setor,
          categoria
        }, getAuthHeaders());
        toast.success('Ativo cadastrado com sucesso');
      }
      
      // Limpar formulário
      setEmpresa('');
      setCodigo('');
      setSetor('');
      setCategoria('');
      
      // Recarregar lista
      carregarEmpresas();
      
      if (onSuccess) onSuccess();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Ativo já cadastrado');
      } else {
        toast.error('Erro ao salvar ativo');
      }
    }
  };

  const handleEdit = (emp) => {
    setEditingId(emp.id);
    setEmpresa(emp.empresa);
    setCodigo(emp.codigo);
    setSetor(emp.setor);
    setCategoria(emp.categoria || '');
    
    // Scroll para o formulário
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEmpresa('');
    setCodigo('');
    setSetor('');
    setCategoria('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este ativo?')) {
      return;
    }

    try {
      await axios.delete(`${API}/empresas/${id}`, getAuthHeaders());
      toast.success('Ativo excluído com sucesso');
      carregarEmpresas();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Erro ao excluir ativo');
    }
  };

  // Função para definir meta - EXCLUSIVIDADE: só pode ter um tipo
  const handleMetaQtdChange = (empresaId, value) => {
    const numValue = parseInt(value) || 0;
    setEditingData(prev => ({
      ...prev,
      [empresaId]: {
        meta_basica: numValue,
        tipo_meta: numValue > 0 ? 'quantidade' : prev[empresaId]?.tipo_meta || 'quantidade'
      }
    }));
  };

  const handleMetaValorChange = (empresaId, value) => {
    const numValue = parseFloat(value) || 0;
    setEditingData(prev => ({
      ...prev,
      [empresaId]: {
        meta_basica: numValue,
        tipo_meta: numValue > 0 ? 'valor' : prev[empresaId]?.tipo_meta || 'valor'
      }
    }));
  };

  const handleClearMeta = async (empresaId, tipoAtual) => {
    try {
      // Limpar a meta atual para permitir trocar o tipo
      await axios.put(`${API}/empresas/${empresaId}`, {
        meta_basica: 0,
        tipo_meta: tipoAtual === 'quantidade' ? 'valor' : 'quantidade'
      }, getAuthHeaders());
      toast.success('Meta limpa. Agora você pode definir o outro tipo.');
      carregarEmpresas();
      
      // Limpar edição
      setEditingData(prev => {
        const newData = { ...prev };
        delete newData[empresaId];
        return newData;
      });
    } catch (error) {
      toast.error('Erro ao limpar meta');
    }
  };

  const handleSaveMeta = async (empresaId) => {
    const data = editingData[empresaId];
    if (!data || data.meta_basica === undefined) return;

    try {
      await axios.put(`${API}/empresas/${empresaId}`, {
        meta_basica: data.meta_basica,
        tipo_meta: data.tipo_meta
      }, getAuthHeaders());
      toast.success('Meta atualizada com sucesso');
      carregarEmpresas();
      
      // Limpar edição
      setEditingData(prev => {
        const newData = { ...prev };
        delete newData[empresaId];
        return newData;
      });
    } catch (error) {
      toast.error('Erro ao atualizar meta');
    }
  };

  // Verificar se um campo deve estar desabilitado baseado na meta atual
  const isQtdDisabled = (emp) => {
    // Se já tem meta de valor definida, desabilitar campo de quantidade
    return emp.tipo_meta === 'valor' && emp.meta_basica > 0;
  };

  const isValorDisabled = (emp) => {
    // Se já tem meta de quantidade definida, desabilitar campo de valor
    return emp.tipo_meta === 'quantidade' && emp.meta_basica > 0;
  };

  // Obter valor para exibição nos campos
  const getMetaQtd = (emp) => {
    if (editingData[emp.id]?.tipo_meta === 'quantidade') {
      return editingData[emp.id]?.meta_basica || '';
    }
    return emp.tipo_meta === 'quantidade' ? (emp.meta_basica || '') : '';
  };

  const getMetaValor = (emp) => {
    if (editingData[emp.id]?.tipo_meta === 'valor') {
      return editingData[emp.id]?.meta_basica || '';
    }
    return emp.tipo_meta === 'valor' ? (emp.meta_basica || '') : '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Gerenciar Cadastro - Exterior
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Formulário */}
        <div className="p-6 border-b border-gray-200 bg-green-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingId ? 'Editar Ativo Internacional' : 'Cadastrar Novo Ativo Internacional'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Empresa *
              </label>
              <input
                type="text"
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                required
                placeholder="Ex: Apple Inc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código (Ticker) *
              </label>
              <input
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                required
                placeholder="Ex: AAPL"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria *
              </label>
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Selecione...</option>
                <option value="Business">Business</option>
                <option value="REIT's">REIT's</option>
              </select>
              {categoria === "REIT's" && (
                <p className="text-xs text-green-600 mt-1">
                  ℹ️ Setor preenchido automaticamente como "Imobiliário"
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Setor *
              </label>
              <input
                type="text"
                value={setor}
                onChange={(e) => setSetor(e.target.value)}
                required
                placeholder="Ex: Technology"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="md:col-span-2 flex gap-3 justify-end">
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar Edição
                </button>
              )}
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
              >
                {editingId ? 'Atualizar' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>

        {/* Aviso sobre metas */}
        <div className="px-6 pt-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            <strong>ℹ️ Regra de Meta:</strong> Cada ativo pode ter apenas <strong>UM tipo de meta</strong> (Quantidade OU Valor em USD). 
            Para trocar o tipo, clique no botão "Limpar" primeiro.
          </div>
        </div>

        {/* Tabela */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Ativos Cadastrados ({empresas.length})
          </h3>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : empresas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum ativo cadastrado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Setor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meta QTD</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meta Valor (USD)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {empresas.map((emp) => (
                    <tr key={emp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{emp.empresa}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">{emp.codigo}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{emp.setor}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          emp.categoria === 'Business' ? 'bg-blue-100 text-blue-800' :
                          emp.categoria === "REIT's" ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {emp.categoria || 'N/A'}
                        </span>
                      </td>
                      {/* Meta QTD */}
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={getMetaQtd(emp)}
                            onChange={(e) => handleMetaQtdChange(emp.id, e.target.value)}
                            onBlur={() => handleSaveMeta(emp.id)}
                            placeholder="QTD"
                            disabled={isQtdDisabled(emp)}
                            className={`w-20 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                              isQtdDisabled(emp) 
                                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
                                : 'border-gray-300'
                            }`}
                          />
                          {isQtdDisabled(emp) && (
                            <button
                              onClick={() => handleClearMeta(emp.id, 'valor')}
                              className="text-xs text-red-500 hover:text-red-700 underline"
                              title="Limpar meta de valor para usar quantidade"
                            >
                              Limpar
                            </button>
                          )}
                        </div>
                      </td>
                      {/* Meta Valor USD */}
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1">
                          <span className={`${isValorDisabled(emp) ? 'text-gray-300' : 'text-gray-500'}`}>$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={getMetaValor(emp)}
                            onChange={(e) => handleMetaValorChange(emp.id, e.target.value)}
                            onBlur={() => handleSaveMeta(emp.id)}
                            placeholder="0.00"
                            disabled={isValorDisabled(emp)}
                            className={`w-24 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                              isValorDisabled(emp) 
                                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
                                : 'border-gray-300'
                            }`}
                          />
                          {isValorDisabled(emp) && (
                            <button
                              onClick={() => handleClearMeta(emp.id, 'quantidade')}
                              className="text-xs text-red-500 hover:text-red-700 underline"
                              title="Limpar meta de quantidade para usar valor"
                            >
                              Limpar
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(emp)}
                            className="text-green-600 hover:text-green-800 p-1"
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(emp.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
